// Photo matching API — the reads, the analyst's decisions and the identity merge
// (ADR-0039, ADR-0040, ADR-0042, ADR-0052).
//
// Every score is the raw cosine similarity. Whether a suggestion is a *suggested
// match* is the API's verdict (`suggested_match`); the UI never re-derives it.
import type { CaseRole } from './cases'
import { apiClient } from './client'

/** One row of the identity list. */
export interface FaceMatchingIdentityRow {
  person_id: string
  display_name: string | null
  photo_url: string | null
  pending_total: number
  /** Pending suggestions that earn the *suggested match* label. */
  suggested_matches: number
  top_score: number
}

export interface FaceMatchingIdentityList {
  identities: FaceMatchingIdentityRow[]
}

/** One confirmed face in the reference strip. */
export interface FaceMatchingReference {
  face_id: string
  crop_url: string | null
  document_id: string | null
  document_name: string | null
  /** The stored page index, 0 for a photo or a recording: `facePageNumber` names it. */
  page: number | null
  reference: boolean
  /** Why the face may be a weak reference — the faces card warns about it. */
  quality_flags: string[]
  uploaded_at?: string | null
  processed_at?: string | null
}

/** One pending candidate in the table. */
export interface FaceMatchingCandidate {
  face_id: string
  crop_url: string | null
  document_id: string | null
  document_name: string | null
  /** The stored page index, 0 for a photo or a recording: `facePageNumber` names it. */
  page: number | null
  /** The image the modal highlights on — for a PDF, the stored page render. */
  source_url: string | null
  box: number[]
  /** The best similarity to the identity's reference faces. */
  score: number
  /** The second-best reference similarity, when a second reference answered. */
  score_second: number | null
  /** The best score of another identity suggested on the same face. */
  rival_score: number | null
  reference_count: number
  suggested_match: boolean
  /** The label's conditions this suggestion misses; empty for a suggested match. */
  unmet_conditions: string[]
  quality_flags: string[]
  status: string
  /**
   * The identity the face is already confirmed to, when it is another one: the
   * candidate then says the two identities may be one person, and the analyst
   * merges them instead of confirming the face (ADR-0042). Null — or absent from
   * an older API — for a face nobody owns yet.
   */
  assigned_person_id?: string | null
  assigned_display_name?: string | null
  /** That identity's main photo: an auth-gated `/api/v1/files/...` URL. */
  assigned_photo_url?: string | null
}

export interface FaceMatchingIdentityDetail {
  person_id: string
  display_name: string | null
  photo_url: string | null
  /** The face the person points at, so the card badges it after a reload. */
  main_face_id: string | null
  references: FaceMatchingReference[]
  /** Suggested matches first, then by similarity. */
  candidates: FaceMatchingCandidate[]
  /** Candidates behind the current filter, across every page. */
  total: number
  page: number
  page_size: number
  /** Every pending candidate of the identity, whatever the filter. */
  pending_total: number
  suggested_matches: number
  /**
   * The caller's role as the request was granted it (a superuser reads as owner),
   * on the read and on every decision's answer. A viewer decides nothing about a
   * face (PRD-0032 SC-036). Absent from an older API.
   */
  member_role?: CaseRole
}

export interface IdentityDetailQuery {
  /** Keep only the suggested matches; omit for every pending candidate. */
  suggestedOnly?: boolean
  page?: number
  pageSize?: number
}

/** Confirm or reject one face-person pair. */
export type FaceDecision = 'confirm' | 'reject'

export const faceMatchingApi = {
  listIdentities: (caseId: string) =>
    apiClient.get<FaceMatchingIdentityList>(`/v1/cases/${caseId}/face-matching/identities`),

  identityDetail: (caseId: string, personId: string, query: IdentityDetailQuery = {}) =>
    apiClient.get<FaceMatchingIdentityDetail>(
      `/v1/cases/${caseId}/face-matching/identities/${personId}`,
      {
        params: {
          suggested_only: query.suggestedOnly || undefined,
          page: query.page,
          page_size: query.pageSize,
        },
      }
    ),

  /**
   * Confirm or reject a pair; the response is the refreshed identity. Like the
   * merge and the reference toggle below, it needs the collaborator role: a 403
   * for a viewer (PRD-0032 SC-036).
   */
  decide: (caseId: string, personId: string, faceId: string, decision: FaceDecision) =>
    apiClient.post<FaceMatchingIdentityDetail>(
      `/v1/cases/${caseId}/face-matching/identities/${personId}/faces/${faceId}/decision`,
      { decision }
    ),

  /**
   * Merge *personId* — the identity *faceId* is suggested for — with the identity
   * the face is confirmed to (ADR-0042), the way the analyst *confirmed* it: which
   * of the two stays and which one goes (ADR-0052). Audited and reversible in the
   * entity-resolution audit; the response is the survivor's refreshed detail. A
   * 409 when the face no longer proposes merging exactly these two or they share
   * a photo, a 404 when the identity is gone, and a 503 `audit_unavailable` —
   * nothing was merged — when a merge that touches a HUMINT subject could not be
   * recorded in the audit trail first (ADR-0044).
   */
  merge: (
    caseId: string,
    personId: string,
    faceId: string,
    confirmed: { survivorId: string; mergedId: string }
  ) =>
    apiClient.post<FaceMatchingIdentityDetail>(
      `/v1/cases/${caseId}/face-matching/identities/${personId}/faces/${faceId}/merge`,
      { survivor_id: confirmed.survivorId, merged_id: confirmed.mergedId }
    ),

  /** Toggle a confirmed face as a reference; the recompute runs in this request. */
  setReference: (caseId: string, personId: string, faceId: string, reference: boolean) =>
    apiClient.put<FaceMatchingIdentityDetail>(
      `/v1/cases/${caseId}/face-matching/identities/${personId}/faces/${faceId}/reference`,
      { reference }
    ),
}
